import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Bookmark, BookmarkCheck, Building2, Share2, Search, DollarSign, Activity, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { useWatchlist } from '../context/WatchlistContext';
import { api } from '../api';

const ratingTone = (r) => {
  if (r >= 5) return 'bg-[#2D5F4F] text-white';
  if (r >= 4) return 'bg-[#4A7C5E] text-white';
  if (r >= 3) return 'bg-[#D4A85C] text-[#1A2622]';
  if (r >= 2) return 'bg-[#D58668] text-white';
  if (r >= 1) return 'bg-[#C75D5D] text-white';
  return 'bg-[#9A9077] text-white';
};

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtInt(n) {
  if (n == null) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

function SurveysBlock({ owner }) {
  const pen = owner.penalties || {};
  const sur = owner.survey || {};
  const hasPen = pen.facilities_with_penalties > 0;
  const hasSur = sur.facilities_with_surveys > 0;
  if (!hasPen && !hasSur) {
    return (
      <div className="rounded-xl bg-white border border-dashed border-[#E5DCC4] p-6 text-center">
        <div className="text-[13px] text-[#7A857F]">Survey and penalty data not yet ingested for this owner. Try again in a moment.</div>
      </div>
    );
  }
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat value={fmtMoney(pen.total_fines)} label="Total federal fines" accent="text-[#C75D5D]"/>
        <Stat value={pen.fines_per_facility != null ? fmtMoney(pen.fines_per_facility) : '—'} label="Fines / facility"/>
        <Stat value={pen.pct_with_penalties != null ? `${pen.pct_with_penalties}%` : '—'} label="Facilities w/ penalties"/>
        <Stat value={sur.deficiencies_per_facility ?? '—'} label="Deficiencies / facility"/>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><DollarSign size={13}/> Federal penalties</div>
          <div className="mt-3">
            <Row k="Total fines" v={fmtMoney(pen.total_fines)}/>
            <Row k="Number of fines" v={fmtInt(pen.fine_count)}/>
            <Row k="Payment denials" v={fmtInt(pen.denial_count)}/>
            <Row k="Total penalty events" v={fmtInt(pen.penalty_count)}/>
            <Row k="Facilities with penalties" v={`${pen.facilities_with_penalties} of ${owner.facilities}`}/>
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><ShieldCheck size={13}/> Health deficiencies</div>
          <div className="mt-3">
            <Row k="Total health deficiencies" v={fmtInt(sur.total_health_deficiencies)}/>
            <Row k="Per facility (avg)" v={sur.deficiencies_per_facility ?? '—'}/>
            <Row k="Abuse, neglect, exploitation" v={fmtInt(sur.abuse_neglect_deficiencies)}/>
            <Row k="Infection control" v={fmtInt(sur.infection_control_deficiencies)}/>
            <Row k="Resident rights" v={fmtInt(sur.resident_rights_deficiencies)}/>
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><Activity size={13}/> Fire safety</div>
          <div className="mt-3">
            <Row k="Total fire safety deficiencies" v={fmtInt(sur.total_fire_deficiencies)}/>
            <Row k="Per facility (avg)" v={sur.facilities_with_surveys ? (sur.total_fire_deficiencies / sur.facilities_with_surveys).toFixed(1) : '—'}/>
            <Row k="Surveys on file" v={`${sur.facilities_with_surveys} of ${owner.facilities}`}/>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

function Row({ k, v, sub }) {
  return (
    <div className="py-2.5 flex justify-between items-start gap-3 border-b border-[#F0E7D2] last:border-0">
      <span className="text-[12.5px] text-[#5A6660]">{k}</span>
      <div className="text-right">
        <div className="text-[12.5px] font-semibold text-[#1A2622]">{v ?? '—'}</div>
        {sub && <div className="text-[10.5px] text-[#A8B0AA] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function FinancialsBlock({ fin }) {
  if (!fin || fin.facilities_with_reports === 0) {
    return (
      <div className="rounded-xl bg-white border border-dashed border-[#E5DCC4] p-6 text-center">
        <DollarSign size={20} className="text-[#A8B0AA] mx-auto"/>
        <div className="mt-2 text-[13px] text-[#7A857F]">No CMS cost report records associated with this owner yet.</div>
      </div>
    );
  }
  const pm = fin.payer_mix || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat value={fmtMoney(fin.net_patient_revenue)} label="Net patient revenue"/>
        <Stat value={fmtMoney(fin.net_income)} label="Net income" accent="text-[#4A7C5E]"/>
        <Stat value={fin.occupancy != null ? `${fin.occupancy}%` : '—'} label="Cost report occupancy"/>
      </div>
      <div className="mt-4 p-5 rounded-xl bg-white border border-[#E5DCC4]">
        <div className="grid grid-cols-1 md:grid-cols-5 items-center gap-4 text-center">
          <div><div className="font-display text-xl text-[#1A2622]">{fmtMoney(fin.net_patient_revenue)}</div><div className="text-[10px] uppercase tracking-wider text-[#7A857F] mt-1">Revenue</div></div>
          <div className="text-xl text-[#C9C0A8]">−</div>
          <div><div className="font-display text-xl text-[#1A2622]">{fmtMoney(fin.operating_expense)}</div><div className="text-[10px] uppercase tracking-wider text-[#7A857F] mt-1">Op. expense</div></div>
          <div className="text-xl text-[#C9C0A8]">=</div>
          <div><div className="font-display text-xl text-[#4A7C5E]">{fmtMoney(fin.net_income)}</div><div className="text-[10px] uppercase tracking-wider text-[#4A7C5E] mt-1">Net income {fin.margin != null ? `· ${fin.margin}%` : ''}</div></div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><DollarSign size={13}/> Balance sheet</div>
          <div className="mt-3">
            <Row k="Total assets" v={fmtMoney(fin.total_assets)}/>
            <Row k="Total liabilities" v={fmtMoney(fin.total_liabilities)}/>
            <Row k="Net (assets − liab.)" v={fmtMoney((fin.total_assets || 0) - (fin.total_liabilities || 0))}/>
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><Activity size={13}/> Operations</div>
          <div className="mt-3">
            <Row k="Resident days" v={fmtInt(fin.total_days)}/>
            <Row k="Total discharges" v={fmtInt(fin.total_discharges)}/>
            <Row k="SNF admissions" v={fmtInt(fin.snf_admissions)}/>
            <Row k="Cost report facilities" v={fin.facilities_with_reports}/>
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><ShieldCheck size={13}/> Payer mix</div>
          <div className="mt-3 mb-3 h-3 w-full rounded-full overflow-hidden bg-[#F0E7D2] flex">
            <div className="bg-[#4A7C5E]" style={{ width: `${pm.medicare_pct || 0}%` }}/>
            <div className="bg-[#7BA88E]" style={{ width: `${pm.medicaid_pct || 0}%` }}/>
            <div className="bg-[#D4A85C]" style={{ width: `${pm.other_pct || 0}%` }}/>
          </div>
          <Row k={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#4A7C5E]"/>Medicare</span>} v={`${fmtInt(fin.medicare_days)} (${pm.medicare_pct || 0}%)`}/>
          <Row k={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#7BA88E]"/>Medicaid</span>} v={`${fmtInt(fin.medicaid_days)} (${pm.medicaid_pct || 0}%)`}/>
          <Row k={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#D4A85C]"/>Other</span>} v={`${fmtInt(fin.other_days)} (${pm.other_pct || 0}%)`}/>
        </div>
      </div>
    </>
  );
}

export default function OwnerDetailPage() {
  const { slug } = useParams();
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const { isWatched, toggle } = useWatchlist();

  useEffect(() => {
    setLoading(true);
    api.owner(slug).then((d) => { setOwner(d); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen"><Navbar />
        <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10 pb-10">
          <div className="text-sm text-[#7A857F]">Loading owner profile…</div>
        </main>
        <Footer />
      </div>
    );
  }
  if (!owner) {
    return (
      <div className="min-h-screen"><Navbar />
        <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Owner not found</h1></main>
      </div>
    );
  }

  const watched = isWatched('owner', owner.slug);
  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'financials', label: 'Financials' },
    { id: 'surveys', label: 'Surveys & Fines' },
    { id: 'facilities', label: 'Facilities' },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Owners', to: '/owners' }, { label: owner.name }]} />

        {/* Header */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#F0E7D2] flex items-center justify-center text-sm font-semibold text-[#2D5F4F]">{owner.initials || '?'}</div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">{owner.type || 'Owner'}</div>
                <h1 className="mt-1 font-display text-4xl font-semibold text-[#1A2622] tracking-tight leading-tight">{owner.name}</h1>
              </div>
            </div>
            <p className="mt-5 text-[14px] text-[#3A4A44] max-w-2xl leading-relaxed">CMS-published ownership associations, aggregate financials, and facility performance for this owner.</p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <a href={`https://www.google.com/search?q=${encodeURIComponent(owner.name + ' nursing home')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-white border border-[#E5DCC4] text-[12.5px] text-[#1A2622] hover:border-[#4A7C5E]"><Search size={13}/> Search Google <ExternalLink size={11}/></a>
              <button onClick={() => toggle({ kind: 'owner', slug: owner.slug, name: owner.name })} className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md text-[12.5px] font-medium ${watched ? 'bg-[#4A7C5E] text-white' : 'bg-[#1A2622] text-[#FBF6EC] hover:bg-[#2D5F4F]'}`}>
                {watched ? <><BookmarkCheck size={13}/> Watching</> : <><Bookmark size={13}/> Watch</>}
              </button>
              <button className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-white border border-[#E5DCC4] text-[12.5px] text-[#3A4A44] hover:border-[#4A7C5E]"><Share2 size={13}/> Share</button>
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-3 gap-4">
            <Stat value={owner.facilities} label="facilities"/>
            <Stat value={owner.states} label="states" accent="text-[#4A7C5E]"/>
            <Stat value={(owner.roles || []).length} label="roles"/>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-[#E5DCC4]">
          <div className="flex items-center gap-1">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-3 text-[13px] border-b-2 transition-colors ${tab === t.id ? 'border-[#4A7C5E] text-[#1A2622] font-semibold' : 'border-transparent text-[#7A857F] hover:text-[#1A2622]'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'overview' && (
          <section className="mt-7">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                <h3 className="font-display text-lg font-semibold text-[#1A2622]">Roles played</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(owner.roles || []).length > 0 ? owner.roles.map((r, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-[#F5EFDD] border border-[#E5DCC4] text-[11.5px] text-[#3A4A44]">{r}</span>
                  )) : <span className="text-[12px] text-[#7A857F]">No roles recorded</span>}
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                <h3 className="font-display text-lg font-semibold text-[#1A2622]">States with facilities ({(owner.state_list || []).length})</h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(owner.state_list || []).map((s) => (
                    <span key={s} className="px-2.5 py-1 rounded-md bg-[#F5EFDD] border border-[#E5DCC4] text-[11px] font-mono text-[#3A4A44]">{s}</span>
                  ))}
                </div>
              </div>
            </div>
            {owner.financials && owner.financials.facilities_with_reports > 0 && (
              <div className="mt-8">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-display text-xl font-semibold text-[#1A2622]">Cost report financials</h2>
                  <span className="text-[11px] text-[#7A857F]">{owner.financials.facilities_with_reports} of {owner.facilities} facilities with cost reports</span>
                </div>
                <FinancialsBlock fin={owner.financials} />
              </div>
            )}
          </section>
        )}

        {tab === 'financials' && (
          <section className="mt-7">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-xl font-semibold text-[#1A2622]">Cost report financials</h2>
              <span className="text-[11px] text-[#7A857F]">{owner.financials ? `${owner.financials.facilities_with_reports} of ${owner.facilities} facilities with cost reports` : 'No reports'}</span>
            </div>
            <FinancialsBlock fin={owner.financials} />
            <p className="mt-5 text-[11.5px] text-[#A8B0AA] leading-relaxed">Aggregated from the CMS Skilled Nursing Facility Cost Report public dataset. Figures sum across all facilities in this owner's portfolio with submitted cost reports.</p>
          </section>
        )}

        {tab === 'surveys' && (
          <section className="mt-7">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-xl font-semibold text-[#1A2622]">Surveys, deficiencies &amp; federal fines</h2>
              <span className="text-[11px] text-[#7A857F]">CMS Penalties + Survey Summary datasets</span>
            </div>
            <SurveysBlock owner={owner}/>
            <p className="mt-5 text-[11.5px] text-[#A8B0AA] leading-relaxed">Aggregated from the CMS Nursing Home Penalties and Survey Summary public datasets. Fines reflect Civil Money Penalties levied across the owner's portfolio.</p>
          </section>
        )}

        {tab === 'facilities' && (
          <section className="mt-7">
            <div className="flex items-center gap-2 mb-4">
              <Building2 size={14} className="text-[#4A7C5E]"/>
              <h2 className="font-display text-xl font-semibold text-[#1A2622]">Facilities</h2>
              <span className="text-[11px] text-[#7A857F]">Showing {(owner.facility_records || []).length} of {owner.facilities}</span>
            </div>
            <ul className="space-y-2">
              {(owner.facility_records || []).map((f) => (
                <li key={f.ccn}>
                  <Link to={`/facilities/${f.slug}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white border border-[#E5DCC4] hover:border-[#4A7C5E] transition-colors">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold ${ratingTone(f.rating)}`}>{f.rating || '—'}</span>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-[#1A2622] truncate">{f.name}</div>
                        <div className="text-[11px] text-[#7A857F] mt-0.5">{f.city}{f.city && f.state ? ', ' : ''}{f.state} {f.chain ? `· ${f.chain}` : ''}</div>
                      </div>
                    </div>
                    {f.occupancy != null && <span className="text-[11.5px] text-[#3A4A44] shrink-0"><b className="text-[#1A2622]">{f.occupancy}%</b> occupied</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
